import { FetchCommandError, TechnicalFetchCommandError } from './Errors.js';

class FetchIt {
	constructor() {
		this.defaultBrowserTimeout = 15000; // Will be overwritten in backstage mockFetch.mockRequest if explicit timeout is given there
	}

	/**
	 * @param url {string}
	 * @param [options={}] {RequestInit & {timeout: number=}}
	 * @returns {Promise<Object|undefined|string>}
	 */
	async get(url, options = {}) {
		const { timeout = this.defaultBrowserTimeout } = options;
		const abortController = new AbortController();
		const timeoutId = window.setTimeout(() => {
			abortController.abort(new TechnicalFetchCommandError('browserTimeout')); // Wir wollen unseren eigenen abort getrennt behandeln können
		}, timeout);

		try {
			// jPar.util.log.devConsole(`FETCH, GET: ${url}`);
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					'x-requested-with': 'XMLHttpRequest',
				},
				signal: abortController.signal,
				...options,
			});
			window.clearTimeout(timeoutId);

			if (!response.ok) {
				// https://thewebdev.info/2021/03/20/how-to-reject-a-promise-with-the-javascript-async-await-syntax/
				if (response.status === 418) {
					throw new TechnicalFetchCommandError('offline');
				} else {
					throw new TechnicalFetchCommandError(response.status);
				}
				// TODO invalid csrf token handling
			}

			const text = await response.text(); // Parse it as text
			if (this.isJson(text)) {
				return this.getJson(text); // Try to parse it as JSON
			} else if (text.includes('loginPage')) {
				throw new TechnicalFetchCommandError('sessionExpired');
			} else {
				return text;
			}
		} catch (error) {
			if (abortController.signal?.aborted) {
				throw abortController.signal.reason ?? new TechnicalFetchCommandError('browserTimeout');
			}
			throw error instanceof FetchCommandError ? error : new TechnicalFetchCommandError(error);
		}
	}

	/**
	 * @param url {string}
	 * @param [data={}] {BodyInit}
	 * @param [options={}] {Omit<RequestInit, 'body'> & {timeout: number=}}
	 * @returns {Promise<object|string>}
	 * @throws {TechnicalFetchCommandError, FetchCommandError}
	 */
	async post(url, data = {}, options = {}) {
		const { timeout = this.defaultBrowserTimeout } = options;
		const abortController = new AbortController();
		const timeoutId = window.setTimeout(() => {
			abortController.abort(new TechnicalFetchCommandError('browserTimeout')); // Wir wollen unseren eigenen abort getrennt behandeln können
		}, timeout);

		try {
			const { headers, ...restOptions } = options;
			// jPar.util.log.devConsole(`FETCH, POST: ${url}`);
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'X-CSRF-TOKEN': jPar.wdk.security.csrf.getToken(),
					'x-requested-with': 'XMLHttpRequest',
					'Content-Type': data instanceof URLSearchParams ? 'application/x-www-form-urlencoded' : 'application/json',
					...headers,
				},
				body: data,
				// credentials: 'include', // TODO brauch man das
				signal: abortController.signal,
				...restOptions, // options always override the complete attributes
			});

			window.clearTimeout(timeoutId);

			if (!response.ok) {
				switch (response.status) {
					case 418:
						throw new TechnicalFetchCommandError('offline');
					case 403:
						throw new TechnicalFetchCommandError('sessionExpired');
					default:
						throw new TechnicalFetchCommandError(response.status); // e.g. String "500"
				}
			}

			const text = await response.text(); // Parse it as text
			if (this.isJson(text)) {
				return this.getJson(text); // Try to parse it as JSON
			} else if (text.includes('loginPage')) {
				throw new TechnicalFetchCommandError('sessionExpired');
			} else {
				return text;
			}
		} catch (error) {
			if (abortController.signal?.aborted) {
				throw abortController.signal.reason ?? new TechnicalFetchCommandError('browserTimeout');
			}

			throw error instanceof FetchCommandError ? error : new TechnicalFetchCommandError(error);
		}
	}

	/**
	 * @param url {string}
	 * @param [data={}] {BodyInit}
	 * @param [options={}] {Omit<RequestInit, 'body'> & {timeout: number=}}
	 * @returns {Promise<object|string>}
	 * @throws {TechnicalFetchCommandError, FetchCommandError}
	 */
	async put(url, data = {}, options = {}) {
		const { timeout = this.defaultBrowserTimeout } = options;
		const abortController = new AbortController();
		const timeoutId = window.setTimeout(() => {
			abortController.abort(new TechnicalFetchCommandError('browserTimeout')); // Wir wollen unseren eigenen abort getrennt behandeln können
		}, timeout);

		try {
			const { headers, ...restOptions } = options;
			// jPar.util.log.devConsole(`FETCH, POST: ${url}`);
			const response = await fetch(url, {
				method: 'PUT',
				headers: {
					'X-CSRF-TOKEN': jPar.wdk.security.csrf.getToken(),
					'x-requested-with': 'XMLHttpRequest',
					'Content-Type': data instanceof URLSearchParams ? 'application/x-www-form-urlencoded' : 'application/json',
					...headers,
				},
				body: data,
				// credentials: 'include', // TODO brauch man das
				signal: abortController.signal,
				...restOptions, // options always override the complete attributes
			});

			window.clearTimeout(timeoutId);

			if (!response.ok) {
				switch (response.status) {
					case 418:
						throw new TechnicalFetchCommandError('offline');
					case 403:
						throw new TechnicalFetchCommandError('sessionExpired');
					default:
						throw new TechnicalFetchCommandError(response.status); // e.g. String "500"
				}
			}

			const text = await response.text(); // Parse it as text
			if (this.isJson(text)) {
				return this.getJson(text); // Try to parse it as JSON
			} else if (text.includes('loginPage')) {
				throw new TechnicalFetchCommandError('sessionExpired');
			} else {
				return text;
			}
		} catch (error) {
			if (abortController.signal?.aborted) {
				throw abortController.signal.reason ?? new TechnicalFetchCommandError('browserTimeout');
			}

			throw error instanceof FetchCommandError ? error : new TechnicalFetchCommandError(error);
		}
	}

	/**
	 * @param url {string}
	 * @param [data={}] {BodyInit}
	 * @param [options={}] {Omit<RequestInit, 'body'> & {timeout: number=}}
	 * @returns {Promise<object|string>}
	 * @throws {TechnicalFetchCommandError, FetchCommandError}
	 */
	async delete(url, data = {}, options = {}) {
		const { timeout = this.defaultBrowserTimeout } = options;
		const abortController = new AbortController();
		const timeoutId = window.setTimeout(() => {
			abortController.abort(new TechnicalFetchCommandError('browserTimeout')); // Wir wollen unseren eigenen abort getrennt behandeln können
		}, timeout);

		try {
			const { headers, ...restOptions } = options;
			// jPar.util.log.devConsole(`FETCH, POST: ${url}`);
			const response = await fetch(url, {
				method: 'DELETE',
				headers: {
					'X-CSRF-TOKEN': jPar.wdk.security.csrf.getToken(),
					'x-requested-with': 'XMLHttpRequest',
					'Content-Type': data instanceof URLSearchParams ? 'application/x-www-form-urlencoded' : 'application/json',
					...headers,
				},
				body: data,
				// credentials: 'include', // TODO brauch man das
				signal: abortController.signal,
				...restOptions, // options always override the complete attributes
			});

			window.clearTimeout(timeoutId);

			if (!response.ok) {
				switch (response.status) {
					case 418:
						throw new TechnicalFetchCommandError('offline');
					case 403:
						throw new TechnicalFetchCommandError('sessionExpired');
					default:
						throw new TechnicalFetchCommandError(response.status); // e.g. String "500"
				}
			}

			const text = await response.text(); // Parse it as text
			if (this.isJson(text)) {
				return this.getJson(text); // Try to parse it as JSON
			} else if (text.includes('loginPage')) {
				throw new TechnicalFetchCommandError('sessionExpired');
			} else {
				return text;
			}
		} catch (error) {
			if (abortController.signal?.aborted) {
				throw abortController.signal.reason ?? new TechnicalFetchCommandError('browserTimeout');
			}

			throw error instanceof FetchCommandError ? error : new TechnicalFetchCommandError(error);
		}
	}

	/**
	 * @param item {any}
	 * @returns {any}
	 */
	tryParseJson(item) {
		if (this.isJson(item)) {
			return this.getJson(item);
		}
		return item;
	}

	/**
	 * @param item {unknown}
	 * @returns {boolean}
	 */
	isJson(item) {
		item = typeof item !== 'string' ? JSON.stringify(item) : item;

		try {
			item = JSON.parse(item);
		} catch {
			return false;
		}

		return typeof item === 'object' && item !== null;
	}

	/**
	 * @param item {unknown}
	 * @returns {object | undefined}
	 */
	getJson(item) {
		item = typeof item !== 'string' ? JSON.stringify(item) : item;
		item = JSON.parse(item);

		if (typeof item === 'object' && item !== null) {
			return item;
		}
	}
}

/* eslint-disable-next-line no-global-assign */ // TODO auf eslint hören und besser machen
fetchIt = new FetchIt();
